import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, throwError, of, map, mergeMap, from } from 'rxjs';
import { environment } from '../../environments/environment';
import { ChoiceListResponse, Form } from '../types/form';
import { Choice } from '../types/form';

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  // Separate signals for request and response forms
  requestForm = signal<Form | null>(null);
  responseForm = signal<Form | null>(null);

  // Separate loading states
  requestFormLoading = signal<boolean>(false);
  responseFormLoading = signal<boolean>(false);

  // Linked forms storage (for LINK field types)
  linkedForms = signal<Map<number, Form | null>>(new Map());
  linkedFormsLoading = signal<Map<number, boolean>>(new Map());

  // Choice lists storage (for SINGLE_CHOICE field types)
  choiceLists = signal<Map<number, Choice[]>>(new Map());
  choiceListsLoading = signal<Map<number, boolean>>(new Map());

  // Clone ID counters (increments for each new duplicate)
  requestFormCloneCounter = signal<number>(0);
  responseFormCloneCounter = signal<number>(0);

  // Duplicated fields tracking (for repeatable fields)
  // Maps original hierarchyId to array of cloneIds
  fieldDuplicates = signal<Map<String, number[]>>(new Map());

  loadRequestForm(hierarchyId: number): Observable<Form> {
    this.requestFormLoading.set(true);
    // Clear linked forms and choice lists cache when loading a new form
    this.linkedForms.set(new Map());
    this.linkedFormsLoading.set(new Map());
    this.fieldDuplicates.set(new Map());
    this.requestFormCloneCounter.set(0);
    // no need to clear choice lists cache when loading a new form, might be already loaded
    // this.choiceLists.set(new Map());
    // this.choiceListsLoading.set(new Map());

    return this.http.get<Form>(
      `${this.apiUrl}/rest/form/get-form-hierarchy/${hierarchyId}`,
    ).pipe(
      tap(form => {
        // Initialize cloneId to null for all fields
        const initializedForm = this.initializeCloneIds(form);
        this.requestForm.set(initializedForm);
        this.requestFormLoading.set(false);
      }),
      catchError(err => {
        this.requestForm.set(null);
        this.requestFormLoading.set(false);
        return throwError(() => err);
      })
    );
  }

  loadResponseForm(hierarchyId: number): Observable<Form> {
    this.responseFormLoading.set(true);
    // Clear linked forms and choice lists cache when loading a new form
    this.linkedForms.set(new Map());
    this.linkedFormsLoading.set(new Map());
    this.fieldDuplicates.set(new Map());
    this.responseFormCloneCounter.set(0);
    // no need to clear choice lists cache when loading a new form, might be already loaded
    // this.choiceLists.set(new Map());
    // this.choiceListsLoading.set(new Map());

    return this.http.get<Form>(
      `${this.apiUrl}/rest/form/get-form-hierarchy/${hierarchyId}`,
    ).pipe(
      tap(form => {
        // Initialize cloneId to null for all fields
        const initializedForm = this.initializeCloneIds(form);
        this.responseForm.set(initializedForm);
        this.responseFormLoading.set(false);
      }),
      catchError(err => {
        this.responseForm.set(null);
        this.responseFormLoading.set(false);
        return throwError(() => err);
      })
    );
  }

  /*
  * Loads a previously saved filled form from the database by document id
  * Returns the form with the values filled in
  * works for request and response forms
  */
  loadFilledForm(id: number, formType: 'request' | 'response'): Observable<Form> {
    if (formType === 'request') {
      this.requestFormLoading.set(true);
      this.requestForm.set(null);
    } else {
      this.responseFormLoading.set(true);
      this.responseForm.set(null);
    }
    return this.http.get<Form>(
      `${this.apiUrl}/rest/document/${id}`,
    ).pipe(
      tap(form => {
        // Transform checkbox/singlechoice string values to booleans/numbers
        const transformedForm = this.transformCheckboxValues(form);

        if (formType === 'request') {
          this.requestFormLoading.set(false);
          this.requestForm.set(transformedForm);
        } else {
          this.responseFormLoading.set(false);
          this.responseForm.set(transformedForm);
        }
      }),
      catchError(err => {
        if (formType === 'request') {
          this.requestForm.set(null);
          this.requestFormLoading.set(false);
        } else {
          this.responseForm.set(null);
          this.responseFormLoading.set(false);
        }
        return throwError(() => err);
      })
    );
  }

  loadLinkedForm(linkFormId: number, formType: 'request' | 'response'): Observable<Form> {
    // Check if already loaded in cache
    if (this.linkedForms().has(linkFormId)) {
      const cached = this.linkedForms().get(linkFormId);
      if (cached) {
        return of(cached);
      }
    }

    // Check if already loading
    if (this.linkedFormsLoading().get(linkFormId)) {
      return of(null as any);
    }

    // Set loading state
    const loadingMap = new Map(this.linkedFormsLoading());
    loadingMap.set(linkFormId, true);
    this.linkedFormsLoading.set(loadingMap);

    return this.http.get<Form>(
      `${this.apiUrl}/rest/form/get-form-hierarchy/${linkFormId}`,
    ).pipe(
      tap(form => {
        // Store the loaded form in cache
        const formsMap = new Map(this.linkedForms());
        formsMap.set(linkFormId, form);
        this.linkedForms.set(formsMap);

        // Update loading state
        const loadingMap = new Map(this.linkedFormsLoading());
        loadingMap.set(linkFormId, false);
        this.linkedFormsLoading.set(loadingMap);

        // Merge the linked form's children into the main form tree
        this.mergeLinkIntoMainTree(linkFormId, form, formType);
      }),
      catchError(err => {
        // Set form to null on error
        const formsMap = new Map(this.linkedForms());
        formsMap.set(linkFormId, null);
        this.linkedForms.set(formsMap);

        // Update loading state
        const loadingMap = new Map(this.linkedFormsLoading());
        loadingMap.set(linkFormId, false);
        this.linkedFormsLoading.set(loadingMap);

        return throwError(() => err);
      })
    );
  }

  loadChoices(choiceListId: number, page: number = 0, size: number = 500): Observable<ChoiceListResponse> {
    // accepts query params: text (for filtering), default value = "",
    //  page, default value = 0,
    //  size, default value = 5,
    //  sortBy, default value = "id",
    //  ascending, default value = true

    return this.http.get<ChoiceListResponse>(
      `${this.apiUrl}/rest/form/choice/${choiceListId}?page=${page}&size=${size}&sortBy=ordinalPosition&ascending=true`,
      {}
    );
  }

  loadChoiceList(choiceGroupId: number): Observable<Choice[]> {
    // Check if already loaded
    if (this.choiceLists().has(choiceGroupId)) {
      // Already loaded, return cached data
      return of(this.choiceLists().get(choiceGroupId) || []);
    }

    if (this.choiceListsLoading().get(choiceGroupId)) {
      // Already loading, return empty observable (component will use signal to track loading)
      return of([]);
    }

    // Set loading state
    const loadingMap = new Map(this.choiceListsLoading());
    loadingMap.set(choiceGroupId, true);
    this.choiceListsLoading.set(loadingMap);

    // Load first page
    return this.loadChoices(choiceGroupId, 0, 500).pipe(
      tap(response => {
        const choices = response.content || [];

        // Sort initial page by ordinalPosition
        choices.sort((a, b) => a.ordinalPosition - b.ordinalPosition);

        // Store initial choices
        const choicesMap = new Map(this.choiceLists());
        choicesMap.set(choiceGroupId, choices);
        this.choiceLists.set(choicesMap);

        // Check if there are more pages to load
        if (response.page.totalPages > 1) {
          this.loadRemainingChoices(choiceGroupId, response.page.totalPages, choices);
        } else {
          // All loaded, clear loading state
          const loadingMap = new Map(this.choiceListsLoading());
          loadingMap.set(choiceGroupId, false);
          this.choiceListsLoading.set(loadingMap);
        }
      }),
      map(response => response.content || []),
      catchError(err => {
        // Clear loading state on error
        const loadingMap = new Map(this.choiceListsLoading());
        loadingMap.set(choiceGroupId, false);
        this.choiceListsLoading.set(loadingMap);
        return throwError(() => err);
      })
    );
  }

  private loadRemainingChoices(choiceGroupId: number, totalPages: number, currentChoices: Choice[]): void {
    const allChoices = [...currentChoices];

    // Create array of page numbers to load (pages 1 to totalPages-1)
    const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 1);

    from(pageNumbers).pipe(
      mergeMap(
        page => this.loadChoices(choiceGroupId, page, 500),
        10 // Max 10 concurrent requests
      )
    ).subscribe({
      next: (response) => {
        const newChoices = response.content || [];
        allChoices.push(...newChoices);

        // Update stored choices progressively as each page loads
        const choicesMap = new Map(this.choiceLists());
        choicesMap.set(choiceGroupId, [...allChoices]);
        this.choiceLists.set(choicesMap);
      },
      error: (err) => {
        console.error('Error loading additional choice pages:', err);
        // Clear loading state on error
        const loadingMap = new Map(this.choiceListsLoading());
        loadingMap.set(choiceGroupId, false);
        this.choiceListsLoading.set(loadingMap);
      },
      complete: () => {
        // Sort all choices by ordinalPosition before finalizing
        allChoices.sort((a, b) => a.ordinalPosition - b.ordinalPosition);

        // Update cache with sorted choices
        const choicesMap = new Map(this.choiceLists());
        choicesMap.set(choiceGroupId, allChoices);
        this.choiceLists.set(choicesMap);

        // Clear loading state
        const loadingMap = new Map(this.choiceListsLoading());
        loadingMap.set(choiceGroupId, false);
        this.choiceListsLoading.set(loadingMap);
      }
    });
  }

  // Merge linked form's children into the LINK field in the main form tree
  private mergeLinkIntoMainTree(linkFormId: number, linkedForm: Form, formType: 'request' | 'response'): void {
    const mainForm = formType === 'request' ? this.requestForm() : this.responseForm();
    if (!mainForm) return;

    const updatedForm = this.mergeLinkRecursive(mainForm, linkFormId, linkedForm.children || []);

    if (formType === 'request') {
      this.requestForm.set(updatedForm);
    } else {
      this.responseForm.set(updatedForm);
    }
  }

  private getDuplicateKey(hierarchyId: number, formInstanceId: string | null): string {
    return `${hierarchyId}::${formInstanceId ?? 'root'}`;
  }

  // Recursively find LINK fields with matching linkFormId and merge children
  private mergeLinkRecursive(form: Form, linkFormId: number, linkedChildren: Form[]): Form {
    // If this is a LINK field with matching linkFormId, merge children
    if (form.type === 'LINK' && form.rules?.linkFormId === linkFormId) {
      const formInstanceId = form.rules?.formInstanceId ?? null;
      return {
        ...form,
        children: linkedChildren.map(child =>
          this.applyFormInstanceIdRecursively(child, formInstanceId)
        )
      };
    }

    // Recursively process children
    return {
      ...form,
      children: form.children && form.children.length > 0
        ? form.children.map(child => this.mergeLinkRecursive(child, linkFormId, linkedChildren))
        : form.children
    };
  }

  private applyFormInstanceIdRecursively(form: Form, formInstanceId: string | null): Form {
    return {
      ...form,
      rules: {
        ...form.rules,
        formInstanceId
      },
      children: form.children && form.children.length > 0
        ? form.children.map(child => this.applyFormInstanceIdRecursively(child, formInstanceId))
        : form.children
    };
  }

  getLinkedForm(linkFormId: number): Form | null {
    return this.linkedForms().get(linkFormId) || null;
  }

  getLinkedFormLoading(linkFormId: number): boolean {
    return this.linkedFormsLoading().get(linkFormId) || false;
  }

  getChoiceList(choiceGroupId: number): Choice[] | null {
    return this.choiceLists().get(choiceGroupId) || null;
  }

  getChoiceListLoading(choiceGroupId: number): boolean {
    return this.choiceListsLoading().get(choiceGroupId) || false;
  }

  previewSentences(form: Form): Observable<string> {
    // api url only here does not end with /api so we have to remove it
    const apiurl = this.apiUrl + '/rest/document/generate-sentence';
    return this.http.post(apiurl, form, {
      responseType: 'text'
    });
  }

  downloadPdf(documentId: number): Observable<void> {
    return this.http.get(`${this.apiUrl}/rest/document/generate-pdf?documentId=${documentId}`, {
      responseType: 'arraybuffer'
    }).pipe(
      tap((arrayBuffer: ArrayBuffer) => {
        // Create a blob from the ArrayBuffer
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });

        // Create a blob URL
        const url = globalThis.URL.createObjectURL(blob);

        // Open the PDF in a new tab
        window.open(url, '_blank');

        // Clean up the blob URL after a short delay to allow the browser to load it
        setTimeout(() => {
          globalThis.URL.revokeObjectURL(url);
        }, 100);
      }),
      map(() => undefined as void)
    );
  }

  // Update a field value in the form tree
  updateFieldValue(hierarchyId: number,
                   cloneId: number | null,
                   value: any,
                   formInstanceId: string | null,
                   formType: 'request' | 'response',
                   parentXorGrouphierarchyId?: number,
                   parentXorGroupCloneId?: number | null,
                   parentXorGroupFormInstanceId?: string | null,
                   p0?: string | null
    ): void {
    const form = formType === 'request' ? this.requestForm() : this.responseForm();
    if (!form) return;

    let updatedForm = this.updateFormRecursive(form, hierarchyId, cloneId, formInstanceId, value);

    // If this field is part of an XOR group, clear sibling values
    // Support 0 and false as valid values that should trigger XOR clearing
    if (parentXorGrouphierarchyId !== undefined && value != null && value !== '') {
      updatedForm = this.clearXorSiblings(
        updatedForm,
        parentXorGrouphierarchyId,
        parentXorGroupCloneId ?? null,
        parentXorGroupFormInstanceId ?? formInstanceId,
        hierarchyId,
        cloneId,
        formInstanceId
      );
    }

    if (formType === 'request') {
      this.requestForm.set(updatedForm);
    } else {
      this.responseForm.set(updatedForm);
    }
  }

  private updateFormRecursive(
    form: Form,
    hierarchyId: number,
    cloneId: number | null,
    formInstanceId: string | null,
    value: any
  ): Form {
    if (
      form.hierarchyId === hierarchyId &&
      (form.rules?.cloneId ?? null) === cloneId &&
      (form.rules?.formInstanceId ?? null) === formInstanceId
    ) {
      // If value is empty, reset all children recursively
      // Don't reset children if value is a number (including 0, which is a valid numeric value)
      const shouldResetChildren = !value && typeof value !== 'number' && form.children?.length > 0;
      const resetChildren = shouldResetChildren
        ? form.children.map(child => this.resetFormValues(child))
        : form.children;

      return { ...form, value, children: resetChildren };
    }

    return {
      ...form,
      children: form.children.map(child =>
        this.updateFormRecursive(child, hierarchyId, cloneId, formInstanceId, value)
      )
    };
  }

  private resetFormValues(form: Form): Form {
    return {
      ...form,
      value: null,
      children: form.children?.length > 0
        ? form.children.map(child => this.resetFormValues(child))
        : form.children
    };
  }

  // Clear all sibling values in an XOR group except the one being set
  private clearXorSiblings(
    form: Form,
    xorGrouphierarchyId: number,
    xorGroupCloneId: number | null,
    xorGroupFormInstanceId: string | null,
    keephierarchyId: number,
    keepCloneId: number | null,
    keepFormInstanceId: string | null
  ): Form {
    if (
      form.hierarchyId === xorGrouphierarchyId &&
      (form.rules?.cloneId ?? null) === xorGroupCloneId &&
      (form.rules?.formInstanceId ?? null) === xorGroupFormInstanceId &&
      form.type === 'PSEUDO_GROUP' &&
      form.relation === 'XOR'
    ) {
      return {
        ...form,
        children: form.children.map(child =>
          child.hierarchyId === keephierarchyId &&
          (child.rules?.cloneId ?? null) === keepCloneId &&
          (child.rules?.formInstanceId ?? null) === keepFormInstanceId
            ? child
            : this.resetFormValues(child)
        )
      };
    }

    // Recursively search for the XOR group
    return {
      ...form,
      children: form.children.map(child =>
        this.clearXorSiblings(
          child,
          xorGrouphierarchyId,
          xorGroupCloneId,
          xorGroupFormInstanceId,
          keephierarchyId,
          keepCloneId,
          keepFormInstanceId
        )
      )
    };
  }

  resetRequestForm(): void {
    this.requestForm.set(null);
  }

  resetResponseForm(): void {
    this.responseForm.set(null);
  }

  resetAllForms(): void {
    this.requestForm.set(null);
    this.responseForm.set(null);
    this.linkedForms.set(new Map());
    this.linkedFormsLoading.set(new Map());
    // this.choiceLists.set(new Map());
    // this.choiceListsLoading.set(new Map());
    this.fieldDuplicates.set(new Map());
    this.requestFormCloneCounter.set(0);
    this.responseFormCloneCounter.set(0);
  }

  // Validate form recursively and return first error
  validateForm(form: Form | null): {
    isValid: boolean;
    firstError: { hierarchyId: number; cloneId: number | null; formInstanceId: string | null } | null;
    errors: FormValidationError[];
  } {
    if (!form) {
      return { isValid: true, firstError: null, errors: [] };
    }

    const errors: FormValidationError[] = [];

    // Skip the root form element and only validate its children (the actual fields)
    // The root is just a container, not an actual field to validate
    if (form.children && form.children.length > 0) {
      for (const child of form.children) {
        this.validateFormRecursive(child, errors);
      }
    }

    return {
      isValid: errors.length === 0,
      firstError: errors.length > 0
        ? {
          hierarchyId: errors[0].hierarchyId,
          cloneId: errors[0].cloneId,
          formInstanceId: errors[0].formInstanceId
        }
        : null,
      errors
    };
  }

  private validateFormRecursive(form: Form, errors: FormValidationError[]): void {
    // Validate XOR group: exactly one child should have a value (if group is required)
    if (form.type === 'PSEUDO_GROUP' && form.relation === 'XOR' && form.children && form.children.length > 0) {
      const filledChildren = form.children.filter(child => {
        // Check if field has a truthy value
        if (child.type === 'CHECKBOX') {
          return child.value === true;
        }
        return child.value && child.value.toString().trim() !== '';
      });

      // If the XOR group itself is required, ensure exactly one child is filled
      if (form.rules?.isRequired) {
        if (filledChildren.length === 0) {
          errors.push({
            hierarchyId: form.hierarchyId,
            cloneId: form.rules?.cloneId ?? null,
            fieldName: form.name,
            formInstanceId: form.rules?.formInstanceId ?? null,
            errors: ['Legalább egy mező kitöltése kötelező']
          });
        } else if (filledChildren.length > 1) {
          errors.push({
            hierarchyId: form.hierarchyId,
            cloneId: form.rules?.cloneId ?? null,
            fieldName: form.name,
            formInstanceId: form.rules?.formInstanceId ?? null,
            errors: ['Csak egy mező tölthető ki']
          });
        }
      } else if (filledChildren.length > 1) {
        // Even if not required, if any child is filled, only one should be filled
        errors.push({
          hierarchyId: form.hierarchyId,
          cloneId: form.rules?.cloneId ?? null,
          fieldName: form.name,
          formInstanceId: form.rules?.formInstanceId ?? null,
          errors: ['Csak egy mező tölthető ki']
        });
      }
    }

    // Only validate if it's a field (not a container/group/pseudo-group)
    // PSEUDO_GROUP is a structural element (section header) and should never be validated
    if (form.type && form.type !== 'GROUP' && form.type !== 'CONTAINER' && form.type !== 'PSEUDO_GROUP') {
      const fieldErrors = this.validateField(form);
      if (fieldErrors.length > 0) {
        errors.push({
          hierarchyId: form.hierarchyId,
          cloneId: form.rules?.cloneId ?? null,
          fieldName: form.name,
          formInstanceId: form.rules?.formInstanceId ?? null,
          errors: fieldErrors
        });
      }
    }

    if (form.rules?.hasDescendantObligation && form.children && form.children.length > 0) {
      const directChildFields = form.children.filter(child => this.isValidatableField(child));
      const hasValidDirectChildValue = directChildFields.some(child => this.hasValidFieldValue(child));

      if (!hasValidDirectChildValue) {
        errors.push({
          hierarchyId: form.hierarchyId,
          cloneId: form.rules?.cloneId ?? null,
          fieldName: form.name,
          formInstanceId: form.rules?.formInstanceId ?? null,
          errors: ['Legalább egy közvetlen gyermekmező kitöltése kötelező']
        });
      }
    }

    // Validate children if:
    // - Parent has a truthy value (matching the display condition in field.html) OR
    // - Parent is a PSEUDO_GROUP (children always visible)
    // Note: This must match the display condition (field().value || field().type === 'PSEUDO_GROUP')
    // to ensure hidden children are not validated
    const hasValue = !!form.value;
    if (form.children && form.children.length > 0 && (hasValue || form.type === 'PSEUDO_GROUP')) {
      for (const child of form.children) {
        this.validateFormRecursive(child, errors);
      }
    }
  }

  private isValidatableField(field: Form): boolean {
    return !!field.type && field.type !== 'GROUP' && field.type !== 'CONTAINER' && field.type !== 'PSEUDO_GROUP';
  }

  private hasValidFieldValue(field: Form): boolean {
    const value = field.value;

    if (value === null || value === undefined || value === false || value === 'false') {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return true;
  }

  private validateField(field: Form): string[] {
    const errors: string[] = [];
    const value = field.value;
    const rules = field.rules;

    if (!rules) return errors;

    // Helper to check if value is empty based on field type
    const isEmpty = (): boolean => {
      if (value === null || value === undefined) return true;
      if (field.type === 'CHECKBOX') return value !== true;
      if (typeof value === 'string') return value.trim() === '';
      return false;
    };

    // Required validation
    if (rules.isRequired && isEmpty()) {
      errors.push('Ez a mező kötelező');
    }

    // Only validate other rules if field has a value and is a string type
    if (!isEmpty() && typeof value === 'string' && value.trim()) {
      // Min/Max character validation for text fields
      if (rules.minChar !== null && value.length < rules.minChar) {
        errors.push(`Minimum ${rules.minChar} karakter szükséges`);
      }
      if (rules.maxChar !== null && value.length > rules.maxChar) {
        errors.push(`Maximum ${rules.maxChar} karakter engedélyezett`);
      }

      // Min/Max value validation for number fields
      if (rules.minVal !== null) {
        const numValue = Number(value);
        if (!Number.isNaN(numValue) && numValue < rules.minVal) {
          errors.push(`Minimum érték: ${rules.minVal}`);
        }
      }
      if (rules.maxVal !== null) {
        const numValue = Number(value);
        if (!Number.isNaN(numValue) && numValue > rules.maxVal) {
          errors.push(`Maximum érték: ${rules.maxVal}`);
        }
      }

      // Whole number validation
      if (rules.isWhole) {
        const numValue = Number(value);
        if (!Number.isNaN(numValue) && !Number.isInteger(numValue)) {
          errors.push('Csak egész számok engedélyezettek');
        }
      }

      // Min/Max date validation for date fields
      // Parse date value - can be string "yyyy-MM-dd" (DATE) or "yyyy-MM" (SHORT_DATE)
      let dateValue: Date | null = null;
      if (value.includes('-')) {
        const parts = value.split('-');
        if (parts.length >= 2) {
          const year = Number(parts[0]);
          const month = Number(parts[1]) - 1; // Month is 0-indexed
          const day = parts.length === 3 ? Number(parts[2]) : 1; // Default day to 1 for SHORT_DATE
          if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            dateValue = new Date(year, month, day);
            dateValue.setHours(0, 0, 0, 0);
          }
        }
      }

      if (dateValue) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Validate min date (days in the past)
        if (rules.minDate !== null) {
          const min = new Date(today);
          min.setDate(min.getDate() - rules.minDate); // Subtract to get past date
          if (dateValue < min) {
            const minStr = this.formatDate(min);
            errors.push(`A dátum nem lehet korábbi, mint ${minStr}`);
          }
        }

        // Validate max date (days in the future)
        if (rules.maxDate !== null) {
          const max = new Date(today);
          max.setDate(max.getDate() + rules.maxDate); // Add to get future date
          if (dateValue > max) {
            const maxStr = this.formatDate(max);
            errors.push(`A dátum nem lehet későbbi, mint ${maxStr}`);
          }
        }
      }
    }

    return errors;
  }

  // Helper method to format date as yyyy-MM-dd
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Initialize cloneId to null for all fields in the form tree
  private initializeCloneIds(form: Form): Form {
    return {
      ...form,
      rules: {
        ...form.rules,
        cloneId: form.rules?.cloneId ?? null // Set to null if undefined
      },
      children: form.children && form.children.length > 0
        ? form.children.map(child => this.initializeCloneIds(child))
        : form.children
    };
  }

  // Transform field values from API format to internal format
  // - Checkbox: string ("true"/"false") → boolean
  // - SingleChoice: string ("123") → number
  private transformCheckboxValues(form: Form): Form {
    let transformedValue = form.value;

    if (form.type === 'CHECKBOX') {
      transformedValue = this.parseCheckboxValue(form.value);
    } else if (form.type === 'SINGLE_CHOICE') {
      transformedValue = this.parseSingleChoiceValue(form.value);
    }

    return {
      ...form,
      value: transformedValue,
      children: form.children && form.children.length > 0
        ? form.children.map(child => this.transformCheckboxValues(child))
        : form.children
    };
  }

  // Convert checkbox value from API (string or boolean) to boolean
  private parseCheckboxValue(value: any): boolean {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return value ?? false;
  }

  // Convert single choice value from API (string or number) to number
  private parseSingleChoiceValue(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'string') {
      const numValue = Number(value);
      return Number.isNaN(numValue) ? null : numValue;
    }
    return typeof value === 'number' ? value : null;
  }

  // Scroll to first invalid field and mark it as touched to show validation state
  scrollToFirstError(hierarchyId: number, cloneId: number | null, formInstanceId: string | null = null): void {
    setTimeout(() => {
      const clonePart = cloneId ?? 'orig';
      const instancePart = formInstanceId ?? 'root';
      const fieldId = `field-${hierarchyId}-${clonePart}-${instancePart}`;
      const element = document.getElementById(fieldId);

      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Find the input element (supports inputs, textareas, selects, and ng-select)
        const input = element.querySelector('input, textarea, select, .ng-select-container') as HTMLElement;
        if (input) {
          input.focus();
          // Dispatch blur event to mark the field as touched, then refocus
          // This ensures the validation error state is displayed
          input.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
          setTimeout(() => input.focus(), 50);
        }
      }
    }, 100);
  }

  // Duplicate a field (for repeatable fields)
  duplicateField(hierarchyId: number, cloneId: number | null, formType: 'request' | 'response', formInstanceId: string | null): void {
    const form = formType === 'request' ? this.requestForm() : this.responseForm();
    if (!form) return;

    // Get and increment clone counter
    const counter = formType === 'request' ? this.requestFormCloneCounter() : this.responseFormCloneCounter();
    const newCloneId = counter + 1;

    if (formType === 'request') {
      this.requestFormCloneCounter.set(newCloneId);
    } else {
      this.responseFormCloneCounter.set(newCloneId);
    }

    // Track this duplicate
    const duplicatesMap = new Map(this.fieldDuplicates());
    const key = this.getDuplicateKey(hierarchyId, formInstanceId);
    const currentDuplicates = duplicatesMap.get(key) || [];
    duplicatesMap.set(key, [...currentDuplicates, newCloneId]);
    this.fieldDuplicates.set(duplicatesMap);

    // Find and clone the field in the form tree
    const updatedForm = this.duplicateFieldRecursive(
      form,
      hierarchyId,
      cloneId,
      formInstanceId,
      newCloneId,
      formType
    );


    if (formType === 'request') {
      this.requestForm.set(updatedForm);
    } else {
      this.responseForm.set(updatedForm);
    }
  }

  // Remove a duplicated field
  removeFieldDuplicate(hierarchyId: number, cloneId: number, formType: 'request' | 'response', formInstanceId: string | null): void {
    const form = formType === 'request' ? this.requestForm() : this.responseForm();
    if (!form) return;

    // Update duplicates map
    const duplicatesMap = new Map(this.fieldDuplicates());
    const key = this.getDuplicateKey(hierarchyId, formInstanceId);
    const currentDuplicates = duplicatesMap.get(key) || [];
    const updatedDuplicates = currentDuplicates.filter(id => id !== cloneId);

    if (updatedDuplicates.length === 0) {
      duplicatesMap.delete(key);
    } else {
      duplicatesMap.set(key, updatedDuplicates);
    }
    this.fieldDuplicates.set(duplicatesMap);

    // Remove the field from the form tree
    const updatedForm = this.removeFieldRecursive(form, hierarchyId, cloneId, formInstanceId);

    if (formType === 'request') {
      this.requestForm.set(updatedForm);
    } else {
      this.responseForm.set(updatedForm);
    }
  }

  // Get duplicate count for a field
  getDuplicateCount(hierarchyId: number, formInstanceId: string | null): number {
    return (this.fieldDuplicates().get(this.getDuplicateKey(hierarchyId, formInstanceId)) || []).length;
  }

  // Check if field is a duplicate (not just a child of a duplicate)
  isDuplicateField(hierarchyId: number, cloneId: number | null, formInstanceId: string | null): boolean {
    // If cloneId is null, it's an original field
    if (cloneId === null) return false;

    // Check if this cloneId is in the duplicates list for this hierarchyId
    const duplicates = this.fieldDuplicates().get(this.getDuplicateKey(hierarchyId, formInstanceId)) || [];
    return duplicates.includes(cloneId);
  }

  // Recursively find field and insert duplicate after all existing duplicates
  private duplicateFieldRecursive(form: Form,
                                  targethierarchyId: number,
                                  targetCloneId: number | null,
                                  targetFormInstanceId: string | null,
                                  newCloneId: number,
                                  formType: 'request' | 'response'
  ): Form {
    // Check if any children match the target field
    if (form.children && form.children.length > 0) {
      // Find the position to insert: after the last field with matching hierarchyId AND cloneId
      let lastMatchingIndex = -1;
      let targetField: Form | null = null;
      let originalField: Form | null = null;

      for (let i = 0; i < form.children.length; i++) {
        const child = form.children[i];
        const childInstanceId = child.rules?.formInstanceId ?? null;

        // Check if this child matches the exact target (hierarchyId AND cloneId)
        if (
          child.hierarchyId === targethierarchyId &&
          (child.rules?.cloneId ?? null) === targetCloneId &&
          childInstanceId === targetFormInstanceId)
        {
          targetField = child;
          lastMatchingIndex = i;
        }
        // Also track the original field (cloneId === null) with same hierarchyId
        if (
          child.hierarchyId === targethierarchyId &&
          (child.rules?.cloneId ?? null) === null &&
          childInstanceId === targetFormInstanceId
        ) {
          originalField = child;
        }
        // Check if this is a duplicate of the same original field (same hierarchyId, different non-null cloneId)
        if (
          child.hierarchyId === targethierarchyId &&
          childInstanceId === targetFormInstanceId
        ) {
          lastMatchingIndex = i; // Update to insert after all duplicates
        }
      }

      // If we found the target field, insert duplicate after the last field with same hierarchyId
      if (targetField && lastMatchingIndex !== -1) {
        const newChildren = [...form.children];
        // Use the original field (cloneId === null) as template if available, otherwise use target field
        const templateField = originalField || targetField;
        const clonedField = this.cloneField(templateField, newCloneId, targetFormInstanceId, formType);
        newChildren.splice(lastMatchingIndex + 1, 0, clonedField);
        return { ...form, children: newChildren };
      }

      // Recursively search in children
      return {
        ...form,
        children: form.children.map(child =>
          this.duplicateFieldRecursive(
            child,
            targethierarchyId,
            targetCloneId,
            targetFormInstanceId,
            newCloneId,
            formType
          )
        )
      };
    }

    return form;
  }

  // Clone a field with new cloneId
  private cloneField(field: Form, newCloneId: number, formInstanceId: string | null, formType: 'request' | 'response'): Form {
    return {
      ...field,
      rules: {
        ...field.rules,
        cloneId: newCloneId, // Set new cloneId for this duplicate
        formInstanceId
      },
      value: null, // Reset value for duplicate
      children: field.children && field.children.length > 0
        ? field.children
          .filter(child => child.rules?.cloneId === null) // Only include original children, not duplicates
          .map(child => this.cloneFieldChildren(child, newCloneId, formInstanceId, formType))
        : field.children
    };
  }

  // Clone field children recursively
  // If child has cloneId null, it inherits parent's cloneId
  // If child has non-null cloneId (was explicitly duplicated), it gets a NEW unique cloneId
  private cloneFieldChildren(field: Form, parentCloneId: number, formInstanceId: string | null, formType: 'request' | 'response'): Form {
    let childCloneId: number;

    if ((field.rules?.cloneId ?? null) === null) {
      // Original child inherits parent's cloneId
      childCloneId = parentCloneId;
    } else {
      // Child was previously duplicated, needs its own NEW cloneId
      const counter = formType === 'request' ? this.requestFormCloneCounter() : this.responseFormCloneCounter();
      childCloneId = counter + 1;

      if (formType === 'request') {
        this.requestFormCloneCounter.set(childCloneId);
      } else {
        this.responseFormCloneCounter.set(childCloneId);
      }

      // Track this as a duplicate
      const duplicatesMap = new Map(this.fieldDuplicates());
      const key = this.getDuplicateKey(field.hierarchyId, formInstanceId);
      const currentDuplicates = duplicatesMap.get(key) || [];
      duplicatesMap.set(key, [...currentDuplicates, childCloneId]);
      this.fieldDuplicates.set(duplicatesMap);
    }

    return {
      ...field,
      rules: {
        ...field.rules,
        cloneId: childCloneId,
        formInstanceId
      },
      value: null,
      children: field.children && field.children.length > 0
        ? field.children
          .filter(child => child.rules?.cloneId === null) // Only include original children, not duplicates
          .map(child => this.cloneFieldChildren(child, childCloneId, formInstanceId, formType))
        : field.children
    };
  }

  // Recursively remove field by composite key (hierarchyId, cloneId)
  private removeFieldRecursive(form: Form, targethierarchyId: number, targetCloneId: number, targetFormInstanceId: string | null): Form {
    if (!form.children || form.children.length === 0) {
      return form;
    }

    // Filter out the target field and recursively process children
    return {
      ...form,
      children: form.children
        .filter(child => !(
          child.hierarchyId === targethierarchyId &&
          (child.rules?.cloneId ?? null) === targetCloneId &&
          (child.rules?.formInstanceId ?? null) === targetFormInstanceId
        ))
        .map(child => this.removeFieldRecursive(
          child,
          targethierarchyId,
          targetCloneId,
          targetFormInstanceId
        ))
    };
  }
}

export interface FormValidationError {
  hierarchyId: number;
  cloneId: number | null;
  formInstanceId: string | null;
  fieldName: string;
  errors: string[];
}
